/**
 * Product line resolution: every self-serve tier (Free · Starter · Growth · Scale)
 * grants the full LandlordOps OS. Tiers differ only by door caps.
 * `restrictToOpsChatOnly` remains an explicit escape hatch for messaging-only accounts.
 */

import {
  Permission,
  TierKey,
  tierHasPermission,
  isFreeTierUser,
} from "../lib/pricing";
import {
  resolveUserTier as _sharedResolveUserTier,
  extractRawPlanKey as _extractRawPlanKey,
} from "@landlordops/shared";
import { isGrandfatheredScaleSuite } from "../lib/grandfathering";

function _rawPlan(userInfo) {
  return _extractRawPlanKey(userInfo || {});
}

export function isOpsChatOnlySubscriber(userInfo) {
  if (!userInfo || typeof userInfo !== "object") return false;
  if (userInfo.restrictToOpsChatOnly === true) return true;
  // Legacy flag only - chat_* SKUs now resolve to full OS with door-band caps.
  return false;
}

/**
 * True only when explicitly restricted to the OpsChat surface.
 * Free / Starter / Growth / Scale / Enterprise all receive full OS access.
 */
export function isLimitedToOpsChatExperience(userInfo) {
  if (!userInfo || typeof userInfo !== "object") return false;
  return userInfo.restrictToOpsChatOnly === true;
}

/** Default app entry for explicitly OpsChat-restricted landlords. */
export const OPS_CHAT_LANDLORD_HOME = "/settings#tenant-chat";

/** True if the user first subscribed via the OpsChat checkout (Firestore `opsChatOnlyIntake`). */
export function isOpsChatOnlyIntakeUser(userInfo) {
  return userInfo?.opsChatOnlyIntake === true;
}

export function isFullLandlordOpsSubscriber(userInfo) {
  if (!userInfo || typeof userInfo !== "object") return false;
  return !isLimitedToOpsChatExperience(userInfo);
}

/**
 * Resolve the normalized tier key for the user's current subscription.
 * Delegates to @landlordops/shared for canonical logic.
 */
export function resolveUserTier(userInfo) {
  return _sharedResolveUserTier(userInfo);
}

/**
 * Check whether the user's current tier grants a specific Permission.
 *
 *   import { Permission } from "../lib/pricing";
 *   if (userHasPermission(userInfo, Permission.RENT_COLLECTION)) { ... }
 */
export function userHasPermission(userInfo, permission) {
  if (!userInfo || typeof userInfo !== "object") return false;
  // Explicit OpsChat-only override: messaging + email alerts only.
  if (userInfo.restrictToOpsChatOnly === true) {
    return (
      permission === Permission.LEO_CHAT ||
      permission === Permission.EMAIL_ALERTS
    );
  }
  if (isFreeTierUser(userInfo)) {
    return tierHasPermission(TierKey.FREE, permission);
  }
  const tier = _sharedResolveUserTier(userInfo);
  return tierHasPermission(tier, permission);
}

/**
 * Every non-restricted plan includes rent collection, vendors, tax prep, etc.
 */
export function hasFullOperatingSystemAccess(userInfo) {
  if (!userInfo || typeof userInfo !== "object") return false;
  if (userInfo.restrictToOpsChatOnly === true) return false;
  return true;
}

/**
 * True when the account is on Scale / Enterprise (or grandfathered) for
 * tenant-chat bulk defaults. Door-cap tier only - not a feature gate.
 */
export function hasSuiteScaleOperatingSystemPlan(userInfo) {
  if (!userInfo || typeof userInfo !== "object") return false;
  if (isGrandfatheredScaleSuite(userInfo)) return true;
  const plan = String(_extractRawPlanKey(userInfo) || "")
    .trim()
    .toLowerCase();
  return plan === "scale" || plan === "enterprise" || plan === "chat_scale";
}

/** Closing Vault ships with the full OS on every tier. */
export function hasClosingVaultAccess(userInfo) {
  return userHasPermission(userInfo, Permission.CLOSING_VAULT);
}

/**
 * Feature gates no longer block Starter/Growth from the OS.
 * Door-cap upgrades are handled by portfolio limit UI instead.
 */
export function shouldShowScaleUpgradeForFullOs(_userInfo) {
  return false;
}

/**
 * SMS and voice escalation ship with the full OS on every tier.
 */
export function hasEscalationAccess(userInfo) {
  return userHasPermission(userInfo, Permission.SMS_ALERTS);
}

/** Routes OpsChat-restricted accounts may use (full app features are blocked).
 *  /units is included because per-unit chat links require a unit to exist.
 *  /communication is included so landlords can view their tenant chat sessions.
 */
export const OPS_CHAT_ACCOUNT_PATH_PREFIXES = [
  "/settings",
  "/profile",
  "/survey/first-impressions",
  "/units",
  "/communication",
  // Booking/availability settings are needed by OpsChat accounts for tour scheduling.
  "/booking-settings",
];

export function isPathAllowedForOpsChatOnly(pathname) {
  return OPS_CHAT_ACCOUNT_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}
